import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Location {
  id: string;
  name: string;
  type: "warehouse" | "store" | "distribution";
  address: string;
  manager: string;
  totalProducts: number;
  totalStock: number;
  stockValue: number;
  lowStockItems: number;
  status: "active" | "inactive";
}

const MOCK_LOCATIONS: Location[] = [
  { id: "LOC-001", name: "Warehouse A", type: "warehouse", address: "123 Industrial Ave, City", manager: "John Doe", totalProducts: 156, totalStock: 2450, stockValue: 85000, lowStockItems: 3, status: "active" },
  { id: "LOC-002", name: "Warehouse B", type: "warehouse", address: "456 Storage Blvd, City", manager: "Jane Smith", totalProducts: 89, totalStock: 1200, stockValue: 42000, lowStockItems: 5, status: "active" },
  { id: "LOC-003", name: "Store Front", type: "store", address: "789 Main Street, Downtown", manager: "Mike Johnson", totalProducts: 78, totalStock: 450, stockValue: 28000, lowStockItems: 2, status: "active" },
  { id: "LOC-004", name: "Distribution Center", type: "distribution", address: "321 Logistics Way, City", manager: "Sarah Wilson", totalProducts: 45, totalStock: 800, stockValue: 35000, lowStockItems: 0, status: "active" },
  { id: "LOC-005", name: "Backup Storage", type: "warehouse", address: "654 Reserve Dr, Suburbs", manager: "Tom Brown", totalProducts: 0, totalStock: 0, stockValue: 0, lowStockItems: 0, status: "inactive" },
];

export default function LocationsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredLocations = MOCK_LOCATIONS.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.manager.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || item.type === filterType;
    return matchesSearch && matchesType;
  });

  const activeLocations = MOCK_LOCATIONS.filter((l) => l.status === "active").length;
  const totalStockValue = MOCK_LOCATIONS.reduce((acc, l) => acc + l.stockValue, 0);
  const totalProducts = MOCK_LOCATIONS.reduce((acc, l) => acc + l.totalProducts, 0);
  const totalLowStock = MOCK_LOCATIONS.reduce((acc, l) => acc + l.lowStockItems, 0);

  const getTypeInfo = (type: string) => {
    switch (type) {
      case "warehouse":
        return { icon: "ri-building-4-line", color: "text-blue-500 bg-blue-500/20", label: t("posInventory.warehouse") };
      case "store":
        return { icon: "ri-store-2-line", color: "text-emerald-500 bg-emerald-500/20", label: t("posInventory.store") };
      case "distribution":
        return { icon: "ri-truck-line", color: "text-violet-500 bg-violet-500/20", label: t("posInventory.distribution") };
      default:
        return { icon: "ri-question-line", color: "text-gray-500 bg-gray-500/20", label: type };
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.locationsTitle")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.locationsSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posInventory.export")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("posInventory.addLocation")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.activeLocations")}</span>
            <i className="ri-map-pin-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{activeLocations}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalProducts")}</span>
            <i className="ri-box-3-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalProducts.toLocaleString()}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalStockValue")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${totalStockValue.toLocaleString()}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.lowStockAlerts")}</span>
            <i className="ri-alarm-warning-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{totalLowStock}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden mb-6`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchLocations")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <option value="all">{t("posInventory.allTypes")}</option>
              <option value="warehouse">{t("posInventory.warehouse")}</option>
              <option value="store">{t("posInventory.store")}</option>
              <option value="distribution">{t("posInventory.distribution")}</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
          {filteredLocations.map((location) => {
            const typeInfo = getTypeInfo(location.type);
            return (
              <div
                key={location.id}
                className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-4 ${themeClasses.tableRowHover} cursor-pointer transition-all`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${typeInfo.color}`}>
                      <i className={`${typeInfo.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className={`font-semibold ${themeClasses.text}`}>{location.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${typeInfo.color}`}>{typeInfo.label}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${location.status === "active" ? "text-emerald-500 bg-emerald-500/20" : "text-gray-500 bg-gray-500/20"}`}>
                    {location.status === "active" ? t("posInventory.active") : t("posInventory.inactive")}
                  </span>
                </div>

                <div className={`flex items-center gap-1 mb-3 ${themeClasses.textSecondary} text-sm`}>
                  <i className="ri-map-pin-2-line"></i>
                  <span className="truncate">{location.address}</span>
                </div>

                <div className={`flex items-center gap-1 mb-4 ${themeClasses.textSecondary} text-sm`}>
                  <i className="ri-user-line"></i>
                  <span>{location.manager}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className={`text-center p-2 rounded-lg ${isDarkMode ? "bg-dark-surface" : "bg-gray-100"}`}>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{location.totalProducts}</p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>{t("posInventory.products")}</p>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${isDarkMode ? "bg-dark-surface" : "bg-gray-100"}`}>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{location.totalStock.toLocaleString()}</p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>{t("posInventory.units")}</p>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${isDarkMode ? "bg-dark-surface" : "bg-gray-100"}`}>
                    <p className={`text-lg font-bold ${location.lowStockItems > 0 ? "text-amber-500" : themeClasses.text}`}>{location.lowStockItems}</p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>{t("posInventory.alerts")}</p>
                  </div>
                </div>

                <div className={`mt-4 pt-3 border-t ${themeClasses.border} flex justify-between items-center`}>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>{t("posInventory.stockValue")}</span>
                  <span className={`font-bold ${themeClasses.text}`}>${location.stockValue.toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
